(() => {
  const INDEX_PAGE_SELECTOR = 'body.is-index [data-hs-page]';
  const REVEAL_TARGET_SELECTOR = '.js-reveal-image';
  const ELEMENT_REVEAL_TARGET_SELECTOR = '[data-element-reveal]';
  const TEXT_REVEAL_TARGET_SELECTOR = '[data-text-reveal]';
  const PERSPECTIVE_STAGE_SELECTOR = '[data-perspective-stage]';
  const FIXED_DISABLE_MEDIA_QUERY = '(min-width: 769px) and (max-aspect-ratio: 1440/800)';
  const MOBILE_MEDIA_QUERY = '(max-width: 768px)';
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const shouldSkipPerspectiveReveal = (target) => {
    if (!(target instanceof Element)) return false;

    // Fixed-stage clones still need reveal setup classes.
    if (target.closest(PERSPECTIVE_STAGE_SELECTOR)) return false;

    const perspectiveSection = target.closest('.p-perspective[data-hs-section]');
    if (!perspectiveSection) return false;

    // Section 01 should animate before entering fixed stage.
    if (perspectiveSection.classList.contains('p-perspective--01')) {
      return false;
    }

    // On mobile, perspective sections should reveal in-place.
    if (window.matchMedia(MOBILE_MEDIA_QUERY).matches) {
      return false;
    }

    // In fixed mode, suppress source-section reveal before stage activation.
    const isFixedDisabled = window.matchMedia(FIXED_DISABLE_MEDIA_QUERY).matches;
    return !isFixedDisabled;
  };

  const setupInertiaScroll = (page) => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const LERP = 0.06;
    const EPSILON = 0.4;

    let rafId = 0;
    let current = page.scrollLeft;
    let target = current;

    const getMaxLeft = () => Math.max(0, page.scrollWidth - page.clientWidth);
    const clampLeft = (value) => Math.min(getMaxLeft(), Math.max(0, value));

    const stopInertia = () => {
      if (!rafId) return;
      window.cancelAnimationFrame(rafId);
      rafId = 0;
      page.classList.remove('is-inertia-scrolling');
    };

    const tick = () => {
      rafId = 0;
      target = clampLeft(target);
      current += (target - current) * LERP;

      if (Math.abs(target - current) < EPSILON) {
        current = target;
      }

      page.scrollLeft = current;

      if (current !== target) {
        rafId = window.requestAnimationFrame(tick);
      } else {
        page.classList.remove('is-inertia-scrolling');
      }
    };

    const requestTick = () => {
      if (rafId) return;
      page.classList.add('is-inertia-scrolling');
      rafId = window.requestAnimationFrame(tick);
    };

    page.addEventListener(
      'wheel',
      (event) => {
        const dominantDelta =
          Math.abs(event.deltaY) >= Math.abs(event.deltaX)
            ? event.deltaY
            : event.deltaX;

        if (dominantDelta === 0) return;

        // Intercept the base wheel handler and drive scroll with inertial motion.
        event.preventDefault();
        event.stopImmediatePropagation();

        target = clampLeft(target + dominantDelta);
        requestTick();
      },
      { passive: false, capture: true }
    );

    page.addEventListener(
      'scroll',
      () => {
        if (rafId) return;
        current = page.scrollLeft;
        target = current;
      },
      { passive: true }
    );

    page.addEventListener(
      'pointerdown',
      () => {
        stopInertia();
        current = page.scrollLeft;
        target = current;
      },
      { passive: true }
    );

    window.addEventListener('resize', () => {
      target = clampLeft(target);
      current = clampLeft(page.scrollLeft);
    });
  };

  const setupImageReveal = (page) => {
    const isDocumentRoot = page === document;
    const observerRoot = isDocumentRoot ? null : page;
    const targets = Array.from(page.querySelectorAll(REVEAL_TARGET_SELECTOR))
      .filter((target) => !shouldSkipPerspectiveReveal(target));
    if (!targets.length) return;

    const peekTargets = targets.filter(
      (target) => target instanceof HTMLElement && target.classList.contains('js-reveal-image--peek')
    );

    const staticRevealTargets = targets.filter(
      (target) => !(target instanceof HTMLElement) || !target.classList.contains('js-reveal-image--peek')
    );

    const immediateTargets = staticRevealTargets.filter(
      (target) =>
        target instanceof HTMLElement &&
        target.matches('.p-overview__image.js-reveal-image') &&
        !target.classList.contains('js-reveal-image--peek')
    );

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      targets.forEach((target) => target.classList.add('is-visible'));
      return;
    }

    const pending = new Set(staticRevealTargets);

    const reveal = (target) => {
      target.classList.add('is-visible');
      pending.delete(target);
    };

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          reveal(entry.target);
          obs.unobserve(entry.target);
        });
      },
      {
        root: observerRoot,
        threshold: 0,
        rootMargin: '0px -12%'
      }
    );

    staticRevealTargets.forEach((target) => {
      if (immediateTargets.includes(target)) return;
      observer.observe(target);
    });

    immediateTargets.forEach((target) => {
      reveal(target);
    });

    const setupPeekRevealProgress = () => {
      if (!peekTargets.length) return;

      const START_VISIBLE_RATIO_X = 0.5;
      const START_VISIBLE_RATIO_Y = 0.6;
      const INTRO_DELAY_MS = 140;
      const INTRO_DURATION_MS = 920;
      const GROWTH_DURATION_MS = 1600;
      const initialLeftMap = new WeakMap();
      let introActivated = false;

      const captureInitialLeft = () => {
        peekTargets.forEach((target) => {
          if (initialLeftMap.has(target)) return;
          const rect = target.getBoundingClientRect();
          initialLeftMap.set(target, rect.left);
        });
      };

      const updatePeekReveal = () => {
        const rootRect = isDocumentRoot
          ? { left: 0, right: window.innerWidth, width: window.innerWidth, top: 0, bottom: window.innerHeight }
          : page.getBoundingClientRect();

        const revealDistance = Math.max(1, rootRect.width * 0.45);
        const growthStartDistance = Math.max(1, rootRect.width * 0.12);
        const effectiveDistance = Math.max(1, revealDistance - growthStartDistance);

        peekTargets.forEach((target) => {
          if (target.dataset.peekGrowDone === '1') {
            target.style.setProperty('--peek-visible-ratio', '1');
            target.style.setProperty('--peek-visible-ratio-x', '1');
            target.style.setProperty('--peek-visible-ratio-y', '1');
            target.classList.add('is-visible');
            return;
          }

          const rect = target.getBoundingClientRect();
          const initialLeft = initialLeftMap.get(target) ?? rect.left;
          const moved = initialLeft - rect.left;
          const progress = clamp((moved - growthStartDistance) / effectiveDistance, 0, 1);

          const easedProgress = progress * progress * (3 - 2 * progress);
          const visibleRatioX = START_VISIBLE_RATIO_X + easedProgress * (1 - START_VISIBLE_RATIO_X);
          const visibleRatioY = START_VISIBLE_RATIO_Y + easedProgress * (1 - START_VISIBLE_RATIO_Y);

          target.style.setProperty('--peek-visible-ratio', String(visibleRatioY));
          target.style.setProperty('--peek-visible-ratio-x', String(visibleRatioX));
          target.style.setProperty('--peek-visible-ratio-y', String(visibleRatioY));

          if (progress > 0) {
            target.dataset.peekGrowDone = '1';
            requestAnimationFrame(() => {
              target.style.setProperty('--peek-visible-ratio', '1');
              target.style.setProperty('--peek-visible-ratio-x', '1');
              target.style.setProperty('--peek-visible-ratio-y', '1');
            });
            target.classList.add('is-visible');
            return;
          }

          if (visibleRatioX >= 0.999 && visibleRatioY >= 0.999) {
            target.classList.add('is-visible');
          } else {
            target.classList.remove('is-visible');
          }
        });
      };

      if (isDocumentRoot) {
        window.addEventListener('scroll', updatePeekReveal, { passive: true });
      } else {
        page.addEventListener('scroll', updatePeekReveal, { passive: true });
      }

      window.addEventListener('resize', updatePeekReveal);
      window.addEventListener('load', updatePeekReveal);

      peekTargets.forEach((target) => {
        // Fallback intro fade handled in JS so behavior works even before SCSS is recompiled.
        target.style.opacity = '0';
        target.style.willChange = 'clip-path, opacity';
        target.style.transition = [
          `opacity ${INTRO_DURATION_MS}ms cubic-bezier(0.2, 0.7, 0.25, 1)`,
          `clip-path ${GROWTH_DURATION_MS}ms cubic-bezier(0.16, 0.84, 0.22, 1)`
        ].join(', ');
      });

      captureInitialLeft();
      updatePeekReveal();

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.setTimeout(() => {
            if (introActivated) return;
            peekTargets.forEach((target) => {
              target.classList.add('is-peek-ready');
              target.style.opacity = '1';
            });
            introActivated = true;
          }, INTRO_DELAY_MS);
        });
      });
    };

    setupPeekRevealProgress();

    // Fallback for environments where IntersectionObserver misses a vertical image.
    const revealByPosition = () => {
      if (!pending.size) return;

      const rootRect = isDocumentRoot
        ? { left: 0, right: window.innerWidth, top: 0, bottom: window.innerHeight, width: window.innerWidth }
        : page.getBoundingClientRect();
      const sideMargin = isDocumentRoot ? 0 : rootRect.width * 0.12;
      const visibleLeft = rootRect.left + sideMargin;
      const visibleRight = rootRect.right - sideMargin;
      const visibleBottom = isDocumentRoot ? window.innerHeight * 0.8 : rootRect.bottom;

      pending.forEach((target) => {
        const rect = target.getBoundingClientRect();
        const overlapsX = rect.right >= visibleLeft && rect.left <= visibleRight;
        const overlapsY = rect.bottom >= rootRect.top && rect.top <= visibleBottom;

        if (overlapsX && overlapsY) {
          reveal(target);
          observer.unobserve(target);
        }
      });
    };

    (isDocumentRoot ? window : page).addEventListener('scroll', revealByPosition, { passive: true });
    window.addEventListener('resize', revealByPosition);
    window.addEventListener('load', revealByPosition);
    revealByPosition();
  };

  const setupElementReveal = (page) => {
    const isDocumentRoot = page === document;
    const observerRoot = isDocumentRoot ? null : page;
    const targets = Array.from(page.querySelectorAll(ELEMENT_REVEAL_TARGET_SELECTOR))
      .filter((target) => target instanceof HTMLElement)
      .filter((target) => !shouldSkipPerspectiveReveal(target));
    if (!targets.length) return;

    targets.forEach((target) => target.classList.add('js-reveal-item'));

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      targets.forEach((target) => target.classList.add('is-visible'));
      return;
    }

    const getRevealDelayMs = (target) => {
      const explicitDelay = Number(target.getAttribute('data-reveal-delay'));
      if (Number.isFinite(explicitDelay) && explicitDelay >= 0) return explicitDelay;

      const kind = target.getAttribute('data-element-reveal');
      if (kind === 'logo') return 720;
      if (kind === 'link') return 180;
      if (kind === 'scroll-cue') return 120;
      if (kind === 'heading') return 40;
      if (kind === 'toc-title') return 0;
      if (kind === 'toc-item') return 140;
      return 0;
    };

    const pending = new Set(targets);

    // Some small UI items can miss horizontal-root observer intersections.
    // Reveal them immediately so they never remain clipped.
    const immediateTargets = targets.filter(
      (target) => target.closest('.c-header__fixed') || target.matches('.c-footer__sns[data-element-reveal="logo"]')
    );
    immediateTargets.forEach((target) => pending.delete(target));

    const deferredFirstViewTargets = targets.filter(
      (target) => target.getAttribute('data-reveal-after') === 'firstview'
    );
    deferredFirstViewTargets.forEach((target) => pending.delete(target));

    const deferredPageLoadTargets = targets.filter(
      (target) => target.getAttribute('data-reveal-after') === 'page-load'
    );
    deferredPageLoadTargets.forEach((target) => pending.delete(target));

    const deferredOverviewHeadingTargets = targets.filter(
      (target) => target.getAttribute('data-reveal-after') === 'overview-heading-complete'
    );
    deferredOverviewHeadingTargets.forEach((target) => pending.delete(target));

    const parseTimeToMs = (value) => {
      const normalized = String(value || '').trim();
      if (!normalized) return 0;
      if (normalized.endsWith('ms')) return Number.parseFloat(normalized) || 0;
      if (normalized.endsWith('s')) return (Number.parseFloat(normalized) || 0) * 1000;
      return 0;
    };

    const getMaxMotionMs = (target) => {
      const nodes = [target, ...target.querySelectorAll('*')];
      let maxMs = 0;

      nodes.forEach((node) => {
        const style = window.getComputedStyle(node);

        const transitionDurations = style.transitionDuration.split(',').map(parseTimeToMs);
        const transitionDelays = style.transitionDelay.split(',').map(parseTimeToMs);
        const transitionCount = Math.max(transitionDurations.length, transitionDelays.length);
        for (let index = 0; index < transitionCount; index += 1) {
          const duration = transitionDurations[index] ?? transitionDurations[transitionDurations.length - 1] ?? 0;
          const delay = transitionDelays[index] ?? transitionDelays[transitionDelays.length - 1] ?? 0;
          maxMs = Math.max(maxMs, duration + delay);
        }

        const animationDurations = style.animationDuration.split(',').map(parseTimeToMs);
        const animationDelays = style.animationDelay.split(',').map(parseTimeToMs);
        const animationCount = Math.max(animationDurations.length, animationDelays.length);
        for (let index = 0; index < animationCount; index += 1) {
          const duration = animationDurations[index] ?? animationDurations[animationDurations.length - 1] ?? 0;
          const delay = animationDelays[index] ?? animationDelays[animationDelays.length - 1] ?? 0;
          maxMs = Math.max(maxMs, duration + delay);
        }
      });

      return maxMs;
    };

    const revealElement = (target) => {
      if (target.classList.contains('is-visible')) return;

      const delayMs = getRevealDelayMs(target);
      if (delayMs > 0) {
        window.setTimeout(() => {
          target.classList.add('is-visible');
        }, delayMs);
      } else {
        target.classList.add('is-visible');
      }

      pending.delete(target);
    };

    const regularObserver = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          revealElement(entry.target);
          obs.unobserve(entry.target);
        });
      },
      {
        root: observerRoot,
        threshold: 0,
        rootMargin: '0px -10%'
      }
    );

    // Links are delayed until they are further inside the viewport.
    const linkObserver = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          revealElement(entry.target);
          obs.unobserve(entry.target);
        });
      },
      {
        root: observerRoot,
        threshold: 0.2,
        rootMargin: '0px -6%'
      }
    );

    targets.forEach((target) => {
      if (
        immediateTargets.includes(target) ||
        deferredFirstViewTargets.includes(target) ||
        deferredPageLoadTargets.includes(target) ||
        deferredOverviewHeadingTargets.includes(target)
      ) return;

      const kind = target.getAttribute('data-element-reveal');
      if (kind === 'link') {
        linkObserver.observe(target);
      } else {
        regularObserver.observe(target);
      }
    });

    immediateTargets.forEach((target) => {
      revealElement(target);
    });

    if (deferredFirstViewTargets.length) {
      const firstViewPrerequisites = Array.from(
        page.querySelectorAll(
          '.p-overview__lead [data-text-reveal], .p-overview__lead [data-element-reveal], .p-overview__image.js-reveal-image'
        )
      ).filter(
        (target) =>
          target instanceof HTMLElement &&
          !deferredFirstViewTargets.includes(target)
      );

      const revealDeferredTargets = () => {
        deferredFirstViewTargets.forEach((target) => revealElement(target));
      };

      if (!firstViewPrerequisites.length) {
        revealDeferredTargets();
      } else {
        let isResolved = false;
        const watchers = [];
        let revealTimerId = 0;

        const resolveOnce = () => {
          if (isResolved) return;
          isResolved = true;
          if (revealTimerId) window.clearTimeout(revealTimerId);
          watchers.forEach((watcher) => watcher.disconnect());
          revealDeferredTargets();
        };

        const scheduleRevealAfterMotions = () => {
          if (revealTimerId) window.clearTimeout(revealTimerId);

          const maxMotionMs = firstViewPrerequisites.reduce(
            (max, target) => Math.max(max, getMaxMotionMs(target)),
            0
          );

          // Start slightly before full settle while keeping a safe margin.
          revealTimerId = window.setTimeout(resolveOnce, Math.max(0, Math.round(maxMotionMs - 200)));
        };

        const checkReady = () => {
          const isReady = firstViewPrerequisites.every((target) => target.classList.contains('is-visible'));
          if (isReady) scheduleRevealAfterMotions();
        };

        firstViewPrerequisites.forEach((target) => {
          const watcher = new MutationObserver(checkReady);
          watcher.observe(target, { attributes: true, attributeFilter: ['class'] });
          watchers.push(watcher);
        });

        // Fallback so cue never stays hidden if a prerequisite fails to reveal.
        window.setTimeout(resolveOnce, 5200);
        checkReady();
      }
    }

    if (deferredPageLoadTargets.length) {
      const revealDeferredPageLoadTargets = () => {
        deferredPageLoadTargets.forEach((target) => revealElement(target));
      };

      if (document.readyState === 'complete') {
        window.setTimeout(revealDeferredPageLoadTargets, 0);
      } else {
        window.addEventListener('load', revealDeferredPageLoadTargets, { once: true });
      }
    }

    if (deferredOverviewHeadingTargets.length) {
      const OVERVIEW_HEADING_SELECTOR = '.p-overview__lead .p-overview__heading[data-text-reveal]';
      const POST_HEADING_DELAY_MS = 100;
      const FALLBACK_TIMEOUT_MS = 7000;
      const revealDeferredOverviewHeadingTargets = () => {
        deferredOverviewHeadingTargets.forEach((target) => revealElement(target));
      };
      const overviewHeading = page.querySelector(OVERVIEW_HEADING_SELECTOR);

      if (!(overviewHeading instanceof HTMLElement)) {
        window.setTimeout(revealDeferredOverviewHeadingTargets, POST_HEADING_DELAY_MS);
      } else {
        let done = false;
        let fallbackTimerId = 0;

        const resolveOnce = () => {
          if (done) return;
          done = true;
          if (fallbackTimerId) window.clearTimeout(fallbackTimerId);
          revealDeferredOverviewHeadingTargets();
        };

        const scheduleAfterHeadingMotion = () => {
          const maxMotionMs = getMaxMotionMs(overviewHeading);
          window.setTimeout(resolveOnce, maxMotionMs + POST_HEADING_DELAY_MS);
        };

        if (overviewHeading.classList.contains('is-visible')) {
          scheduleAfterHeadingMotion();
        } else {
          const watcher = new MutationObserver(() => {
            if (!overviewHeading.classList.contains('is-visible')) return;
            watcher.disconnect();
            scheduleAfterHeadingMotion();
          });
          watcher.observe(overviewHeading, { attributes: true, attributeFilter: ['class'] });

          fallbackTimerId = window.setTimeout(() => {
            watcher.disconnect();
            resolveOnce();
          }, FALLBACK_TIMEOUT_MS);
        }
      }
    }

    if (isDocumentRoot) return;

    // Fallback for environments where IntersectionObserver with horizontal root is unstable.
    const revealByPosition = () => {
      if (!pending.size) return;

      const rootRect = page.getBoundingClientRect();
      const sideMargin = rootRect.width * 0.1;
      const visibleLeft = rootRect.left + sideMargin;
      const visibleRight = rootRect.right - sideMargin;

      pending.forEach((target) => {
        const rect = target.getBoundingClientRect();
        const overlapsX = rect.right >= visibleLeft && rect.left <= visibleRight;
        const overlapsY = rect.bottom >= rootRect.top && rect.top <= rootRect.bottom;

        if (overlapsX && overlapsY) {
          revealElement(target);
          regularObserver.unobserve(target);
          linkObserver.unobserve(target);
        }
      });
    };

    page.addEventListener('scroll', revealByPosition, { passive: true });
    window.addEventListener('resize', revealByPosition);
    window.addEventListener('load', revealByPosition);
    revealByPosition();
  };

  const decodeText = (value) => {
    const parser = document.createElement('div');
    parser.innerHTML = value;
    return parser.textContent || parser.innerText || '';
  };

  const extractLinesFromHtml = (html) => {
    return html
      .replace(/<br\s*\/?\s*>/gi, '\n')
      .split('\n')
      .map((line) => decodeText(line).replace(/\u00a0/g, ' ').trim())
      .filter((line) => line.length > 0);
  };

  const createRevealUnit = (text, delayMs) => {
    const unit = document.createElement('span');
    unit.className = 'js-reveal-text-unit';
    unit.style.setProperty('--reveal-delay', `${delayMs}ms`);
    unit.textContent = text;
    return unit;
  };

  const prepareTextRevealTarget = (target) => {
    const mode = (target.getAttribute('data-text-reveal') || '').trim();
    if (mode !== 'word' && mode !== 'line') return false;

    const lines = extractLinesFromHtml(target.innerHTML);
    if (!lines.length) return false;

    const label = lines.join(' ').replace(/\s+/g, ' ').trim();
    if (label) target.setAttribute('aria-label', label);

    const fragment = document.createDocumentFragment();
    const unitCount = mode === 'line'
      ? lines.length
      : lines.reduce((count, line) => count + line.split(/\s+/).filter((word) => word.length > 0).length, 0);

    const baseDelayStep = mode === 'word' ? 62 : 170;
    const minSpan = mode === 'word' ? 860 : 760;
    const maxDelayStep = mode === 'word' ? 240 : 320;
    const startDelay = mode === 'word' ? 120 : 160;

    const dynamicDelayStep = unitCount > 1
      ? clamp(Math.round(minSpan / (unitCount - 1)), baseDelayStep, maxDelayStep)
      : 0;

    let unitIndex = 0;

    lines.forEach((line, lineIndex) => {
      if (mode === 'line') {
        fragment.appendChild(createRevealUnit(line, startDelay + unitIndex * dynamicDelayStep));
        unitIndex += 1;
      } else {
        const words = line.split(/\s+/).filter((word) => word.length > 0);
        words.forEach((word, wordIndex) => {
          fragment.appendChild(createRevealUnit(word, startDelay + unitIndex * dynamicDelayStep));
          unitIndex += 1;
          if (wordIndex < words.length - 1) {
            fragment.appendChild(document.createTextNode(' '));
          }
        });
      }

      if (lineIndex < lines.length - 1) {
        fragment.appendChild(document.createElement('br'));
      }
    });

    target.textContent = '';
    target.appendChild(fragment);
    target.classList.add('js-reveal-text');
    return true;
  };

  const setupTextReveal = (page) => {
    const isDocumentRoot = page === document;
    const observerRoot = isDocumentRoot ? null : page;
    const targets = Array.from(page.querySelectorAll(TEXT_REVEAL_TARGET_SELECTOR));
    if (!targets.length) return;

    const preparedTargets = targets
      .filter((target) => target instanceof HTMLElement)
      .filter((target) => prepareTextRevealTarget(target));

    if (!preparedTargets.length) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      preparedTargets.forEach((target) => target.classList.add('is-visible'));
      return;
    }

    const observableTargets = preparedTargets.filter(
      (target) => !shouldSkipPerspectiveReveal(target)
    );

    if (!observableTargets.length) return;

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          entry.target.classList.add('is-visible');
          obs.unobserve(entry.target);
        });
      },
      {
        root: observerRoot,
        threshold: 0.2,
        rootMargin: '0px -6%'
      }
    );

    observableTargets.forEach((target) => observer.observe(target));
  };

  const initAnimations = () => {
    const indexPage = document.querySelector(INDEX_PAGE_SELECTOR);
    const isIndexPage = indexPage instanceof HTMLElement;
    const page = isIndexPage ? indexPage : document;

    if (isIndexPage) {
      setupInertiaScroll(page);
    }
    setupImageReveal(page);
    setupElementReveal(page);
    setupTextReveal(page);
  };

  window.addEventListener('DOMContentLoaded', initAnimations);
})();
